import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import api from '@/lib/api';
import { HelpCircle, Send, Loader2, Clock, CheckCircle2, MessageSquare, ArrowLeft, Lock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', category: 'general', priority: 'medium' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [view, setView] = useState('list'); // list, new, thread

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await api.get('/support/tickets');
      setTickets(data.items || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(loadTickets, 10000);
    return () => clearInterval(interval);
  }, [loadTickets]);

  // Update selected ticket when tickets refresh
  useEffect(() => {
    if (selectedTicket) {
      const updated = tickets.find(t => t.id === selectedTicket.id);
      if (updated) setSelectedTicket(updated);
    }
  }, [tickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error('Please fill in subject and message'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/support/tickets', form);
      toast.success('Ticket submitted! Check your email for confirmation.');
      setForm({ subject: '', message: '', category: 'general', priority: 'medium' });
      setSelectedTicket(data);
      setView('thread');
      loadTickets();
    } catch (err) { toast.error('Failed to submit ticket'); }
    finally { setSubmitting(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/reply`, { message: replyText });
      setReplyText('');
      toast.success('Reply sent');
      loadTickets();
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const openThread = (ticket) => { setSelectedTicket(ticket); setView('thread'); };

  const priorityBadge = { urgent: 'destructive', high: 'destructive', medium: 'secondary', low: 'outline' };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 rounded-xl" /></div>;

  // Thread View
  if (view === 'thread' && selectedTicket) {
    return (
      <div data-testid="support-page" className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView('list')}><ArrowLeft className="h-4 w-4" /></Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{selectedTicket.subject}</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant={selectedTicket.status === 'open' ? 'default' : 'outline'} className="text-[10px]">{selectedTicket.status}</Badge>
              <Badge variant={priorityBadge[selectedTicket.priority]} className="text-[10px]">{selectedTicket.priority}</Badge>
              <span>{selectedTicket.category}</span>
              <span>#{selectedTicket.id?.slice(0, 8)}</span>
            </div>
          </div>
        </div>

        {/* Messages Thread */}
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardContent className="p-0">
            <ScrollArea className="h-[400px] p-4">
              <div className="space-y-3">
                {selectedTicket.messages?.map((msg, i) => (
                  <div key={i} className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] p-3 rounded-xl text-sm ${msg.sender === 'admin' ? 'bg-primary/10 border border-primary/20' : 'bg-muted'}`}>
                      <p className="text-xs font-medium mb-1">{msg.sender_name || (msg.sender === 'admin' ? 'VendorSuite Support' : 'You')}</p>
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Reply */}
        {selectedTicket.status !== 'closed' ? (
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardContent className="p-4">
              <div className="flex gap-2">
                <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." rows={2} className="flex-1" />
                <Button onClick={handleReply} disabled={sending || !replyText.trim()} className="shrink-0 self-end">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-xl border-dashed">
            <CardContent className="p-4 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Lock className="h-4 w-4" /> This ticket is closed.
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // New Ticket View
  if (view === 'new') {
    return (
      <div data-testid="support-page" className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView('list')}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Support Ticket</h1>
        </div>
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Subject *</Label><Input data-testid="support-subject-input" value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} placeholder="Brief description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="ai_error">AI Extraction Error</SelectItem>
                      <SelectItem value="technical">Technical Issue</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="compliance">Compliance Question</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm(f => ({...f, priority: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Message *</Label><Textarea data-testid="support-message-input" value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder="Describe your issue..." rows={5} /></div>
              <Button type="submit" data-testid="support-submit-button" className="w-full" disabled={submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                {submitting ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ticket List View (default)
  return (
    <div data-testid="support-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Help & Support</h1>
          <p className="text-sm text-muted-foreground">{tickets.length} tickets</p>
        </div>
        <Button onClick={() => setView('new')} data-testid="support-new-ticket-button">
          <MessageSquare className="mr-2 h-4 w-4" /> New Ticket
        </Button>
      </div>

      {tickets.length === 0 ? (
        <Card className="rounded-xl"><CardContent className="py-16 text-center"><HelpCircle className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" /><p className="text-sm text-muted-foreground">No tickets yet</p><Button variant="outline" className="mt-4" onClick={() => setView('new')}>Submit your first ticket</Button></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <Card key={t.id} className="rounded-xl shadow-[var(--shadow-soft)] cursor-pointer hover:shadow-[var(--shadow-pop)] transition-shadow duration-200" onClick={() => openThread(t)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium truncate">{t.subject}</span>
                      <Badge variant={t.status === 'open' ? 'default' : 'outline'} className="text-[10px] shrink-0">{t.status}</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant={priorityBadge[t.priority]} className="text-[9px]">{t.priority}</Badge>
                      <span>{t.category}</span>
                      <span>{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                      <span className="flex items-center gap-0.5 ml-auto"><MessageSquare className="h-3 w-3" /> {t.messages?.length || 0}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
